import { decodeCheckoutResponse, hasPendingEvents } from 'common/checkout-utils';
import { error, ValidationError } from 'common/errors';
import {
  getRememberMeType,
  pushCheckoutRequest,
  pushCheckoutResponse,
  pushErrors,
  pushVisaRiskStepUp
} from 'common/gamp';
import { reset, saveFlow, saveMeta, savePutIntentResponse } from 'communicator/store';
import {
  getAccessToken,
  getIsFeatureToggleEnabled,
  getUnBindAppInstance,
  isTransactionFidoEligible
} from 'communicator/store/selectors';

import generateXAuthCodeFromAccessToken from 'communicator/utils/generate-x-auth-code';
import { markEnd } from 'communicator/utils/instrumentation';
import postCheckoutRecogonisedUser from 'communicator/utils/recogonized-user';
import unbind from 'communicator/utils/unbind';
import {
  ErrorCode,
  PurchaseIntentThreeDsTwoResponse,
  PurchaseIntentIntegratedIssuerOTPResponse,
  PurchaseIntentResponse,
  GampRiskData
} from 'types/api/intent';
import { AuthenticationDecision, WindowType } from 'types/data';
import { AuthenticationMethod, AuthenticationReason } from 'types/emvco';
import { FetchResponseValue } from 'types/fetch';
import { RememberMeType } from 'types/gamp';
import { CheckoutCommunicatorPayload, CheckoutData } from 'types/visa-sdk/checkout';

type CompleteCheckoutLegacyOptions = {
  putIntentResponse: FetchResponseValue<PurchaseIntentResponse>;
  checkoutData: CheckoutData;
};

export default async function completeCheckoutLegacy(
  { checkoutData, putIntentResponse }: CompleteCheckoutLegacyOptions,
  launchSRCBankApp: (authenticationMethod: AuthenticationMethod) => void,
  launchSRCIssuerOtp: (authenticationMethod: AuthenticationMethod) => void,
  launchCvv: (launchWithoutStepLoad?: boolean) => void,
  launchDcf: () => void,
  launchFIDO: ((fidoAppScreen: 'auth' | 'register') => void) | null,
  launchThreeDs: ((idvUrl: string, windowType?: WindowType) => void) | null,
  launchThreeDsWithOnLoad: ((idvUrl: string) => void) | null,
  launchVPP:
    | ((
        requestPayload: string,
        VppUcheckoutUrlri: string,
        authDecision: AuthenticationDecision
      ) => void)
    | null,
  launchWithoutStepLoad?: boolean
): Promise<CheckoutCommunicatorPayload | ValidationError> {
  const stime = Date.now();
  const accessToken = getAccessToken()?.token as string;

  const isIssuerOtpFlowFullyIntegrated = (data: PurchaseIntentIntegratedIssuerOTPResponse) => {
    const decodedCheckoutResponse = decodeCheckoutResponse(data.checkoutResponse);
    const customOutputData = decodedCheckoutResponse?.customOutputData;

    if (!customOutputData) {
      return false;
    }

    const digitalCardDataFromCheckoutResponse = decodedCheckoutResponse?.maskedCard.digitalCardData;

    const result =
      customOutputData?.riskDecision === 'REQUIRED_ISSUER_CTF' &&
      hasPendingEvents(data.checkoutResponse)
        ? digitalCardDataFromCheckoutResponse?.pendingEvents.includes(
            'PENDING_CARDHOLDER_AUTHENTICATION'
          ) && digitalCardDataFromCheckoutResponse.status === 'PENDING'
        : false;

    return result;
  };

  const executeDcfFlow = async ({
    isCardEnrollment,
    authContextReasons,
    rememberMe,
    riskDecision
  }: GampRiskData) => {
    try {
      // PUT /intent response to DCF
      saveFlow({
        toRoute: 'step-up'
      });

      savePutIntentResponse(putIntentResponse);

      launchDcf();

      // Convert access token to x-auth-code so DCF can have its own access token
      await generateXAuthCodeFromAccessToken({
        accessToken,
        mark: 'checkout_integrated_auth_code_from_access_token'
      });

      saveMeta({
        // appLoaded event waits until this flag is set to return configs to the app
        isCheckoutDataReady: true
      });

      pushVisaRiskStepUp(
        isCardEnrollment,
        rememberMe,
        authContextReasons,
        riskDecision,
        Date.now() - stime
      );
    } catch (err: unknown) {
      // START GENAI@GHCOPILOT
      const errorMessage = (err as Error).message || 'CHECKOUT_FAILURE';
      pushErrors('COMPLETE_CHECKOUT_LEGACY', errorMessage);
      markEnd('checkout_integrated_put_intent', {
        doFlush: true,
        failure: true,
        reason: errorMessage
      });
      // END GENAI@GHCOPILOT
    } finally {
      // This is to ensure if the intent call is aborted. This happens when cancel is pressed on loader screen.
      return {};
    }
  };

  markEnd('checkout_integrated_put_intent', {
    failure: !putIntentResponse.ok,
    reason: putIntentResponse.ok ? undefined : 'CHECKOUT_FAILURE'
  });

  if (!putIntentResponse.ok) {
    // Workaround for TLC2P4-321, should be replaced by a more reliable solution
    // This is a status override to allow users switching cards if 3DS was DECLINED.
    // Note: User canceling challenge will also result in DECLINE therefore the override.
    if (putIntentResponse.errorCode === ErrorCode.DECLINED_3DS2) {
      // START GENAI@GHCOPILOT
      pushErrors('COMPLETE_CHECKOUT_LEGACY', 'CHECKOUT_FAILURE: ErrorCode.DECLINED_3DS2');
      // END GENAI@GHCOPILOT
      return {
        payload: {
          dcfActionCode: 'CHANGE_CARD',
          assuranceData: (putIntentResponse.errorObject as PurchaseIntentThreeDsTwoResponse)
            .assuranceData
        }
      };
    } else if (
      /* New error codes introduced for t2d and cardholder verification declined */
      putIntentResponse.errorCode === ErrorCode.T2D_DECLINED ||
      putIntentResponse.errorCode === ErrorCode.CV_DECLINED
    ) {
      // START GENAI@GHCOPILOT
      pushErrors(
        'COMPLETE_CHECKOUT_LEGACY',
        'CHECKOUT_FAILURE: ErrorCode.T2D_DECLINED or ErrorCode.CV_DECLINED'
      );

      // END GENAI@GHCOPILOT
      return {
        payload: {
          dcfActionCode: 'CHANGE_CARD'
        }
      };
    }

    /* This is to ensure if the intent call is aborted. This happens when cancel is pressed on loader screen.
    We send back a CHANGE_CARD dcfActionCode in this case.  */

    if ((putIntentResponse as unknown as Error).name === 'AbortError') {
      // START GENAI@GHCOPILOT
      pushErrors('COMPLETE_CHECKOUT_LEGACY', 'CHECKOUT_FAILURE: AbortError');
      // END GENAI@GHCOPILOT
      return {
        payload: {
          dcfActionCode: 'CHANGE_CARD',
          unbindAppInstance: getUnBindAppInstance()
        }
      };
    }

    // START GENAI@GHCOPILOT
    pushErrors('COMPLETE_CHECKOUT_LEGACY', 'CHECKOUT_FAILURE: Unknown error');
    // END GENAI@GHCOPILOT
    return error('CHECKOUT_FAILURE');
  }

  let authContextAuthReasons: AuthenticationReason | 'UNDEFINED' = 'UNDEFINED';

  if (
    (
      putIntentResponse.data as PurchaseIntentThreeDsTwoResponse
    ).maskedCard?.digitalCardData.pendingEvents.includes('PENDING_CARDHOLDER_AUTHENTICATION')
  ) {
    const authenticationMethods = (putIntentResponse.data as PurchaseIntentThreeDsTwoResponse)
      .maskedCard?.digitalCardData.authenticationMethods;

    const authenticationStep = (putIntentResponse.data as PurchaseIntentThreeDsTwoResponse)
      .maskedCard?.digitalCardData.authenticationStep;

    if (authenticationMethods) {
      let checkoutUrl;
      let threeDsUrl;
      let authDecision;
      let requestPayload;

      authContextAuthReasons =
        checkoutData.authenticationContext?.authenticationReasons?.[0] || 'UNDEFINED';

      for (const authMethod of authenticationMethods) {
        if (authMethod.authenticationMethodType === '3DS' && authMethod.uriData?.uri) {
          try {
            const url = new URL(authMethod.uriData.uri);
            threeDsUrl = url.toString();

            if (getIsFeatureToggleEnabled('stepupAppDomain')) {
              saveMeta({ threeDsUrl });
            }

            break;
          } catch (_) {
            // START GENAI@GHCOPILOT
            pushErrors(
              'COMPLETE_CHECKOUT_LEGACY',
              'CHECKOUT_FAILURE: stepup app domain Invalid URL'
            );
            // END GENAI@GHCOPILOT
          }
        } else if (authMethod.authenticationMethodType === 'FIDO2' && authMethod.uriData?.uri) {
          if (authMethod.uriData.uri === 'register' || authMethod.uriData.uri === 'auth') {
            checkoutUrl = authMethod.uriData.uri;
            break;
          } else if (
            (authenticationStep?.authenticationDecision === AuthenticationDecision.VPP_AUTH ||
              authenticationStep?.authenticationDecision === AuthenticationDecision.VPP_BINDING) &&
            authMethod.authenticationCredentialReference &&
            getIsFeatureToggleEnabled('enableVpps')
          ) {
            authDecision = authenticationStep.authenticationDecision;
            checkoutUrl = authMethod.uriData.uri;
            requestPayload = authMethod.authenticationCredentialReference;
            break;
          }
        }
      }

      if (launchThreeDs && threeDsUrl) {
        savePutIntentResponse(putIntentResponse);

        // START GENAI@GHCOPILOT
        if (
          getIsFeatureToggleEnabled('enableNonDcfCvv') &&
          !isTransactionFidoEligible() &&
          launchThreeDsWithOnLoad
        ) {
          launchThreeDsWithOnLoad(threeDsUrl);
          return {};
        }

        // END GENAI@GHCOPILOT

        launchThreeDs(threeDsUrl, authenticationStep?.windowType);
      } else if (
        launchFIDO &&
        checkoutUrl &&
        (checkoutUrl === 'register' || checkoutUrl === 'auth')
      ) {
        savePutIntentResponse(putIntentResponse);
        if (accessToken) {
          await generateXAuthCodeFromAccessToken({
            accessToken,
            mark: 'checkout_legacy_auth_code_from_access_token'
          });
        }

        launchFIDO(checkoutUrl === 'register' ? 'register' : 'auth');
      } else if (
        launchVPP &&
        checkoutUrl &&
        requestPayload &&
        (authDecision === AuthenticationDecision.VPP_AUTH ||
          authDecision === AuthenticationDecision.VPP_BINDING)
      ) {
        savePutIntentResponse(putIntentResponse);
        launchVPP(requestPayload, checkoutUrl, authDecision);
      } else {
        // START GENAI@GHCOPILOT
        pushErrors('COMPLETE_CHECKOUT_LEGACY', 'CHECKOUT_FAILURE: No launch method available');
        // END GENAI@GHCOPILOT
        return error('CHECKOUT_FAILURE');
      }

      return {};
    }
  }

  const isCardEnrollment =
    typeof checkoutData.encryptedCard !== 'undefined' && checkoutData.encryptedCard !== '';

  const rememberMe: RememberMeType = getRememberMeType(checkoutData.complianceSettings);

  pushCheckoutRequest(isCardEnrollment, rememberMe, authContextAuthReasons);

  switch (putIntentResponse.data.riskDecision) {
    case 'REQUIRED_CVV': {
      savePutIntentResponse(putIntentResponse);
      if (accessToken) {
        await generateXAuthCodeFromAccessToken({
          accessToken,
          mark: 'checkout_legacy_auth_code_from_access_token'
        });
      }

      if (getIsFeatureToggleEnabled('enableNonDcfCvv')) {
        launchCvv(launchWithoutStepLoad);
      } else {
        return executeDcfFlow({
          authContextReasons: authContextAuthReasons,
          isCardEnrollment,
          rememberMe,
          riskDecision: putIntentResponse.data.riskDecision
        });
      }

      return {};
    }
    case 'REQUIRED_VISA_OTP':
    case 'REQUIRED3DS':
    case 'REQUIRED_BIOMETRIC':
    case 'REQUIRED_ISSUER_CTF': // DCF will now be launched to handle step up sceanrios and finish the checkout. Will pass the
      // PUT /intent response to DCF

      return executeDcfFlow({
        authContextReasons: authContextAuthReasons,
        isCardEnrollment,
        rememberMe,
        riskDecision: putIntentResponse.data.riskDecision
      });

    case 'SUCCESS_BIOMETRICS':
    case 'SUCCESS_CVV':
    case 'SUCCESS_VISA_OTP':
    case 'ALLOW':
    default: {
      if (
        getIsFeatureToggleEnabled('enabledIntegratedIssuerFlow') &&
        isIssuerOtpFlowFullyIntegrated(
          putIntentResponse.data as PurchaseIntentIntegratedIssuerOTPResponse
        )
      ) {
        const decodedCheckoutResponse = decodeCheckoutResponse(
          putIntentResponse.data.checkoutResponse
        );

        const customOutputData = decodedCheckoutResponse?.customOutputData;
        const isSRCIOchestration = customOutputData?.srciOrchestartedAuthentication === true;
        const digitalCardDataFromCheckoutResponse =
          decodedCheckoutResponse?.maskedCard.digitalCardData;

        const authenticationMethod = hasPendingEvents(putIntentResponse.data.checkoutResponse)
          ? digitalCardDataFromCheckoutResponse.authenticationMethods[0]
          : null;

        if (getIsFeatureToggleEnabled('enabledSRCBankApp')) {
          savePutIntentResponse(putIntentResponse);
          if (
            authenticationMethod?.authenticationMethodType === 'APP_AUTHENTICATION' &&
            isSRCIOchestration === false
          ) {
            if (accessToken) {
              await generateXAuthCodeFromAccessToken({
                accessToken,
                mark: 'checkout_legacy_auth_code_from_access_token'
              });
            }

            launchSRCBankApp(
              hasPendingEvents(putIntentResponse.data.checkoutResponse)
                ? digitalCardDataFromCheckoutResponse.authenticationMethods[0]
                : null
            );

            return {};
          }
        }

        if (getIsFeatureToggleEnabled('enabledSRCIsssuerOtpFlow')) {
          savePutIntentResponse(putIntentResponse);
          if (
            (authenticationMethod?.authenticationMethodType === 'SMS_OTP' ||
              authenticationMethod?.authenticationMethodType === 'EMAIL_OTP') &&
            isSRCIOchestration === false
          ) {
            if (accessToken) {
              await generateXAuthCodeFromAccessToken({
                accessToken,
                mark: 'checkout_legacy_auth_code_from_access_token'
              });
            }

            launchSRCIssuerOtp(
              hasPendingEvents(putIntentResponse.data.checkoutResponse)
                ? digitalCardDataFromCheckoutResponse.authenticationMethods[0]
                : null
            );

            return {};
          }
        }

        savePutIntentResponse(putIntentResponse);
        return {
          payload: {
            checkoutResponse: putIntentResponse.data.checkoutResponse,
            ...(checkoutData.idToken && { idToken: checkoutData.idToken }),
            dcfActionCode: 'PENDING_AUTHENTICATION'
            // unBindAppInstance is deprecated in EMVCo v1.3
          }
        };
      }
      // Recognized users should skip all remember_me/ERM/SSI calls since they
      // already have cookies.

      // This handles the series of steps that need to be performed w.r.t remember_me cookies
      postCheckoutRecogonisedUser();
    }
  }

  const unbindAppInstance = getUnBindAppInstance();

  if (!hasPendingEvents(putIntentResponse.data.checkoutResponse)) {
    if (unbindAppInstance) {
      unbind({ accessToken });
    }

    reset();
  }

  pushCheckoutResponse(
    putIntentResponse.ok,
    isCardEnrollment,
    rememberMe,
    authContextAuthReasons,
    Boolean(putIntentResponse.data.riskDecision),
    Date.now() - stime
  );

  return {
    // TODO: callid and payloadSignature when handling visa-srci
    payload: {
      ...putIntentResponse.data,
      ...(checkoutData.idToken && { idToken: checkoutData.idToken }),
      dcfActionCode: 'COMPLETE',
      unbindAppInstance
    }
  };
}
